import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, FileText, Cable, Loader2, Database, Mail, Upload as UploadIcon, AlertCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { rassegne, connectors } from '../lib/data';
const providerMeta: Record<string, {
  label: string;
  icon: ComponentType<any>;
}> = {
  mimesi: {
    label: 'Mimesi',
    icon: Database
  },
  eco_stampa: {
    label: "L'Eco della Stampa",
    icon: Database
  },
  data_stampa: {
    label: 'Data Stampa',
    icon: Database
  },
  email: {
    label: 'Email',
    icon: Mail
  },
  upload: {
    label: 'Upload manuale',
    icon: UploadIcon
  }
};
function KPI({
  label,
  value,
  icon: Icon,
  accent = false
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  accent?: boolean;
}) {
  return <div className="bg-card border border-border rounded-lg p-6 flex flex-col gap-3" data-id="element-562">
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-563">
        <Icon className={`h-4 w-4 ${accent ? 'text-accent animate-spin-slow' : ''}`} data-id="element-564" />
        {label}
      </div>
      <div className={`text-3xl md:text-4xl font-semibold leading-none ${accent ? 'text-accent' : 'text-foreground'}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-565">
        {value}
      </div>
    </div>;
}
function getStatusColor(status: string) {
  if (status === 'connected') return '#4A7C59';
  if (status === 'error') return '#B85450';
  return '#8B8680';
}
function getStatusLabel(status: string) {
  if (status === 'connected') return 'Connesso';
  if (status === 'error') return 'Errore';
  return 'Disconnesso';
}
export function Homepage() {
  const navigate = useNavigate();
  // Calculate aggregates
  const totalRassegne = rassegne.length;
  const totalArticles = rassegne.reduce((acc, r) => acc + r.articleCount, 0);
  const activeConnectors = connectors.filter(c => c.status === 'connected').length;
  const processingRassegne = rassegne.filter(r => r.status === 'processing').length;
  // Campaign summary aggregates
  const weightedScoreSum = rassegne.reduce((acc, r) => acc + r.avgScore * r.articleCount, 0);
  const avgScore = totalArticles > 0 ? weightedScoreSum / totalArticles : 0;
  const totalAVE = rassegne.reduce((acc, r) => acc + r.totalAVE, 0);
  const sentiment = rassegne.reduce((acc, r) => ({
    positive: acc.positive + r.sentimentMix.positive,
    neutral: acc.neutral + r.sentimentMix.neutral,
    negative: acc.negative + r.sentimentMix.negative
  }), {
    positive: 0,
    neutral: 0,
    negative: 0
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-566">
      {/* Header */}
      <header data-id="element-567">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" data-id="element-568">
          Panoramica
        </h1>
        <p className="text-base text-muted-foreground" data-id="element-569">
          Attività e stato del sistema
        </p>
      </header>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-570">
        <KPI label="Rassegne caricate" value={totalRassegne} icon={Newspaper} data-id="element-571" />
        <KPI label="Articoli estratti" value={totalArticles} icon={FileText} data-id="element-572" />
        <KPI label="Connettori attivi" value={activeConnectors} icon={Cable} data-id="element-573" />
        <KPI label="In elaborazione" value={processingRassegne} icon={Loader2} accent data-id="element-574" />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-575">
        {/* Left Column: Connectors */}
        <div className="space-y-6" data-id="element-576">
          <div className="bg-card border border-border rounded-lg flex flex-col h-full" data-id="element-577">
            <div className="px-6 pt-6 pb-4" data-id="element-578">
              <h3 className="text-lg font-semibold" data-id="element-579">Stato connettori</h3>
            </div>
            <div className="divide-y divide-border flex-1" data-id="element-580">
              {connectors.map(connector => {
              const meta = providerMeta[connector.id];
              const Icon = meta?.icon || Cable;
              return <div key={connector.id} className="py-5 px-6 flex items-center gap-4" data-id="element-581">
                    <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-582">
                      <Icon className="h-4 w-4 text-muted-foreground" data-id="element-583" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-584">
                      <div className="flex items-center justify-between mb-1" data-id="element-585">
                        <h4 className="font-medium text-base truncate pr-4" data-id="element-586">
                          {connector.name}
                        </h4>
                        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-587">
                          <div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: getStatusColor(connector.status)
                      }} data-id="element-588" />
                          <span className="text-sm text-muted-foreground" data-id="element-589">
                            {getStatusLabel(connector.status)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground" data-id="element-590">
                        <span data-id="element-591">
                          Ultima sinc:{' '}
                          {connector.lastSync ? new Date(connector.lastSync).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'Mai'}
                        </span>
                        <span style={{
                      fontVariantNumeric: 'tabular-nums'
                    }} data-id="element-592">
                          {connector.rassegneIngested} rassegne
                        </span>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Right Column: Recent Activity */}
        <div className="space-y-6" data-id="element-593">
          <div className="bg-card border border-border rounded-lg flex flex-col h-full" data-id="element-594">
            <div className="flex items-center justify-between px-6 pt-6 pb-4" data-id="element-595">
              <h3 className="text-lg font-semibold" data-id="element-596">Attività recenti</h3>
              <button onClick={() => navigate('/rassegne')} className="text-sm text-accent hover:underline flex items-center gap-1" data-id="element-597">
                Vedi tutte
                <ArrowRight className="h-3 w-3" data-id="element-598" />
              </button>
            </div>

            <div className="divide-y divide-border flex-1" data-id="element-599">
              {rassegne.slice(0, 6).map(r => {
              const meta = providerMeta[r.provider];
              const Icon = meta?.icon || Database;
              return <button key={r.id} onClick={() => navigate(`/rassegne/${r.id}`)} className="w-full text-left py-4 px-6 hover:bg-muted/30 transition-colors" data-id="element-600">
                    <div className="flex items-center gap-4" data-id="element-601">
                      <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-602">
                        <Icon className="h-4 w-4 text-muted-foreground" data-id="element-603" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-604">
                        <h3 className="font-medium text-[15px] leading-tight mb-1.5 truncate" data-id="element-605">
                          {r.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-id="element-606">
                          <span data-id="element-607">{meta?.label}</span>
                          <span data-id="element-608">·</span>
                          <span data-id="element-609">{r.dateRange}</span>
                          {r.status === 'processing' && <>
                              <span data-id="element-610">·</span>
                              <div className="flex items-center gap-1 text-accent" data-id="element-611">
                                <Loader2 className="h-3 w-3 animate-spin" data-id="element-612" />
                                <span data-id="element-613">In elaborazione</span>
                              </div>
                            </>}
                          {r.status === 'error' && <>
                              <span data-id="element-614">·</span>
                              <div className="flex items-center gap-1 text-destructive" data-id="element-615">
                                <AlertCircle className="h-3 w-3" data-id="element-616" />
                                <span data-id="element-617">Errore</span>
                              </div>
                            </>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-618">
                        <div className="text-lg font-semibold leading-none" style={{
                      fontVariantNumeric: 'tabular-nums'
                    }} data-id="element-619">
                          {r.articleCount}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 uppercase tracking-wider" data-id="element-620">
                          Articoli
                        </div>
                      </div>
                    </div>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Full-width: Campaign Summary */}
      <div className="bg-card border border-border rounded-lg p-6" data-id="element-621">
        <h3 className="text-lg font-semibold mb-6" data-id="element-622">Andamento campagna</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-border" data-id="element-623">
          <div className="flex flex-col gap-2 md:px-6 first:px-0 pt-4 md:pt-0 first:pt-0" data-id="element-624">
            <div className="text-sm text-muted-foreground" data-id="element-625">AVE totale</div>
            <div className="text-3xl font-semibold" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-626">
              €{(totalAVE / 1000).toFixed(0)}k
            </div>
          </div>

          <div className="flex flex-col gap-2 md:px-6 pt-4 md:pt-0" data-id="element-627">
            <div className="text-sm text-muted-foreground" data-id="element-628">Score medio</div>
            <div className="flex items-center gap-3" data-id="element-629">
              <div className="text-3xl font-semibold text-accent" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-630">
                {avgScore.toFixed(1)}
              </div>
              <TrendingUp className="h-5 w-5 text-accent" data-id="element-631" />
            </div>
          </div>

          <div className="flex flex-col gap-2 md:px-6 pt-4 md:pt-0" data-id="element-632">
            <div className="text-sm text-muted-foreground" data-id="element-633">
              Sentiment (Pos / Neu / Neg)
            </div>
            <div className="flex items-baseline gap-2 leading-none mt-1" data-id="element-634">
              <span className="text-3xl font-semibold text-[#4A7C59]" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-635">
                {sentiment.positive}
              </span>
              <span className="text-xl text-muted-foreground" data-id="element-636">/</span>
              <span className="text-2xl text-muted-foreground" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-637">
                {sentiment.neutral}
              </span>
              <span className="text-xl text-muted-foreground" data-id="element-638">/</span>
              <span className="text-2xl text-[#B85450]" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-639">
                {sentiment.negative}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}